import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { GlobalConstants } from '../common/global';
import { Observable, of, map, tap, finalize, shareReplay, catchError } from 'rxjs';
import { GdprApiResponse, GdprProfilePatchBody, GdprProfileQuestion } from "../components/gdpr-modal/gdpr-modal.models";
import { FormBuilder, FormGroup } from "@angular/forms";

@Injectable({ providedIn: 'root' })
export class GdprSubscriptionsService {
  private _gdprTextId = signal<number | null>(null);
  private _loading = signal<boolean>(false);
  private _error = signal<string | null>(null);

  private _cache$?: Observable<GdprProfileQuestion[]>;
  private _lastUserId?: number;

  readonly gdprTextId = computed(() => this._gdprTextId());
  readonly loading = computed(() => this._loading());
  readonly error = computed(() => this._error());

  constructor(private readonly http: HttpClient, private fb: FormBuilder) {}

  private loadStatus(userId: number, forceReload = false): Observable<GdprProfileQuestion[]> {
    if (!forceReload && this._cache$ && this._lastUserId === userId) {
      return this._cache$;
    }

    this._lastUserId = userId;
    const url = `${environment.apiUrl}/v1/main/user/gdpr/${userId}?connId=${environment.connId}`;

    this._loading.set(true);
    this._error.set(null);

    this._cache$ = this.http.get<GdprApiResponse>(url, { headers: GlobalConstants.headers.headers }).pipe(
      tap(response => {
        if (!response.success) {
          throw new Error('GDPR request failed');
        }
      }),
      map(response => {
        this._gdprTextId.set(response.data.gdprText?.gdprTextId ?? null);
        return response.data.gdprQuestions.gdprQuestionItems.filter(question => !question.required) as GdprProfileQuestion[];
      }),
      catchError(() => {
        this._gdprTextId.set(null);
        this._error.set('Failed to load GDPR questions');
        return of<GdprProfileQuestion[]>([]); //If there is any error we simply won't display this to User
      }),
      finalize(() => {
        this._loading.set(false);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this._cache$;
  }

  getOptionalQuestionsForm(userId: number, forceReload = false): Observable<FormGroup> {
    return this.loadStatus(userId, forceReload).pipe(
      map(questions => this.createFormGroup(questions))
    );
  }

  refreshOptionalQuestionsForm(userId: number): Observable<FormGroup> {
    this._cache$ = undefined;
    this._lastUserId = undefined;
    return this.getOptionalQuestionsForm(userId, true);
  }

  private createFormGroup(questions: GdprProfileQuestion[]): FormGroup {
    const optionsFormArray = this.fb.array(
      questions.map(question =>
        this.fb.group({
          questionId: [question.questionId],
          questionText: [question.questionText],
          answered: [!!question.answered]
        })
      )
    );
    return this.fb.group({ options: optionsFormArray });
  }

  patchQuestion(gdprTextId: number, questionId: number, answered: boolean) {
    const url = `${environment.apiUrl}/v1/main/user/gdpr?connId=${environment.connId}`;

    const body: GdprProfilePatchBody = {
      gdprTextId,
      gdprQuestionItem: [{ questionId, answered }]
    };

    return this.http.patch<{ success: boolean, data?: boolean }>(url, body, { headers: GlobalConstants.headers.headers }).pipe(
      map(response =>
        response.success
      ),
      catchError(() =>
        of(false)
      )
    );
  }

  clear(): void {
    this._gdprTextId.set(null);
    this._error.set(null);
    this._loading.set(false);
    this._lastUserId = undefined;
    this._cache$ = undefined;
  }
}
